import type { OrderControl } from "./segmenter-elements.js";
import type { WordOrder } from "./segmented-words.js";

/**
 * The controls that order the word list, and what they say about it.
 *
 * Apart from the view and from the tool because both need them and neither
 * owns them: the view marks which one is on and the tool acts on a click, and
 * a reader sees one set of buttons.
 */

/**
 * Mark which order the list is in, for the page and for a reader.
 *
 * `aria-sort` goes on the column header, which is the element the attribute
 * belongs to. `aria-pressed` goes on every button, including the one outside
 * the table, so a reader using a screen reader is told which control is on
 * whether or not it heads a column.
 */
export function showOrder(
  controls: readonly OrderControl[],
  order: WordOrder,
): void {
  for (const control of controls) {
    const chosen = control.order === order;

    control.heading?.setAttribute("aria-sort", chosen ? "descending" : "none");
    control.button.setAttribute("aria-pressed", String(chosen));
    control.button.classList.toggle("cb-segmenter-ordered", chosen);
  }
}

/** Wire every ordering control to ask for the order it names. */
export function wireOrdering(
  controls: readonly OrderControl[],
  onOrder: (order: WordOrder) => void,
): void {
  for (const control of controls) {
    control.button.addEventListener("click", () => {
      onOrder(control.order);
    });
  }
}
